import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { authenticate } from './api';
import api from './api';
import { I18nProvider } from './i18n/index.jsx';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Onboarding from './pages/Onboarding';
import InvoicesOut from './pages/InvoicesOut';
import InvoicesIn from './pages/InvoicesIn';
import Cash from './pages/Cash';
import Applications from './pages/Applications';
import KartaPobytuNew from './pages/KartaPobytuNew';
import KartaPobytu from './pages/KartaPobytu';
import AiChat from './pages/AiChat';
import Documents from './pages/Documents';
import CompanySelect from './pages/CompanySelect';
import ContractSign from './pages/ContractSign';
import GatewaySign from './pages/GatewaySign';
import WorkContractSign from './pages/WorkContractSign';
import Withdrawals from './pages/Withdrawals';
import Demo from './pages/Demo';
import OnboardingFlow from './pages/OnboardingFlow';
import AdminClients from './pages/AdminClients';
import AdminCompanies from './pages/AdminCompanies';
import AdminApplications from './pages/AdminApplications';
import AdminContracts from './pages/AdminContracts';
import AdminPayouts from './pages/AdminPayouts';
import AdminRequests from './pages/AdminRequests';
import AdminDiagnostic from './pages/AdminDiagnostic';
import AdminAudit from './pages/AdminAudit';
import AdminMetrics from './pages/AdminMetrics';
import Notifications from './pages/Notifications';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminUsers from './pages/AdminUsers';
import SuperAdminFinance from './pages/SuperAdminFinance'; 

import PaymentWall from './pages/PaymentWall';
import AdminGatewayPayments from './pages/AdminGatewayPayments';
import AdminInvoices from './pages/AdminInvoices';
import MyAnalytics from './pages/MyAnalytics';
import Security from './pages/Security';
import MyContracts from './pages/MyContracts';
import AdminHierarchy from './pages/AdminHierarchy';
import TopUp from './pages/TopUp';
import Exchange from './pages/Exchange';
import MyTeam from './pages/MyTeam';
import PaymentLinks from './pages/PaymentLinks';
import Support from './pages/Support';
import AdminSupport from './pages/AdminSupport';
import Transactions from './pages/Transactions';
import Receipts from './pages/Receipts';
import AdminReceipts from './pages/AdminReceipts';
import AdminAccounting from './pages/AdminAccounting';
import AdminBankImport from './pages/AdminBankImport';
import TaxDashboard from './pages/TaxDashboard';
import MyDashboard from './pages/MyDashboard';
import MyFinance from './pages/MyFinance';
import MyTaxes from './pages/MyTaxes';
import AdminIncomingInvoices from './pages/AdminIncomingInvoices';
import Guide from './pages/Guide';
import DemoMode from './pages/DemoMode';
import KsefSettings from './pages/KsefSettings';
import StripeSettings from './pages/StripeSettings';
import BankAccounts from './pages/BankAccounts';
import Analytics from './pages/Analytics';
import Achievements from './pages/Achievements';
import Leaderboard from './pages/Leaderboard';
import QRPayment from './pages/QRPayment';
import Calendar from './pages/Calendar';
import InvoiceTemplates from './pages/InvoiceTemplates';
import Subscriptions from './pages/Subscriptions';
import VoiceInvoice from './pages/VoiceInvoice';
import AiCalls from './pages/AiCalls';
import CompanyRoles from './pages/CompanyRoles';
import ApiDocs from './pages/ApiDocs';
import CountrySelect from './pages/CountrySelect';
import Proforma from './pages/Proforma';
import OwnerInvoiceCreate from './pages/OwnerInvoiceCreate';
import OwnerInvoices from './pages/OwnerInvoices';
import WorkPermits from './pages/WorkPermits';
// Legacy: WorkPermitApplication, AdminWorkPermits → replaced by unified WorkPermits
import ContractTemplates from './pages/ContractTemplates';
import LegalDocuments from './pages/LegalDocuments';
import AdminBroadcast from './pages/AdminBroadcast';
import MonthlyRachunki from './pages/MonthlyRachunki';
import ClientOnboarding from './pages/ClientOnboarding';
import AiAssistant from './pages/AiAssistant';
import TierSelect from './pages/TierSelect';
import EcosystemModeSelect from './pages/EcosystemModeSelect';
import OwnCompanyOnboarding from './pages/OwnCompanyOnboarding';
import CompanyTeam from './pages/CompanyTeam';
import HREmployees from './pages/HREmployees';
import HRLeaves from './pages/HRLeaves';
import ZusDashboard from './pages/ZusDashboard';
import AdminTier2 from './pages/AdminTier2';
import DashboardOwner from './pages/DashboardOwner';
import OwnerCit8 from './pages/OwnerCit8';
import AccountingRestore from './pages/AccountingRestore';
import A1Application from './pages/A1Application';
import Counterparties from './pages/Counterparties';
import PartnerContracts from './pages/PartnerContracts';
import AdminPartnerContracts from './pages/AdminPartnerContracts';
import SuperAdminSelect from './pages/SuperAdminSelect';
import WebLogin from './pages/WebLogin';
import AccountingWorkspace from './components/AccountingWorkspace';
import AccountingOverview from './pages/AccountingOverview';
import AccountantDashboard from './pages/AccountantDashboard';
import AccountantReports from './pages/AccountantReports';
import CompanyBooks from './pages/CompanyBooks';
import OffersList from './pages/OffersList';
import OfferDetails from './pages/OfferDetails';
import OfferAccept from './pages/OfferAccept';
import OfferPublish from './pages/OfferPublish';
import GenroEntry from './pages/GenroEntry';
import BottomNav from './components/BottomNav';
import LanguageSwitcher from './components/LanguageSwitcher';
import Loading from './components/Loading';
import ErrorScreen from './components/ErrorScreen';
import { returnToAdmin } from './utils/impersonation';

export const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const ADMIN_ROLES = ['superadmin', 'company_admin'];

// Superuser telegram IDs who can toggle test mode
const SUPERUSER_IDS = [7812065632]; // @TheAwakenedOleks

export default function App() {
  const [user, setUser] = useState(null);
  const [page, setPageRaw] = useState('dashboard');
  const [pageHistory, setPageHistory] = useState(['dashboard']);

  // Guard: block navigation during onboarding + track history
  const setPage = (newPage, isBack = false) => {
    // If user hasn't completed onboarding, only allow onboarding + demo (+ proforma if trial enabled)
    const isImpersonating = localStorage.getItem('impersonating') === 'true' || !!localStorage.getItem('admin_token') || !!localStorage.getItem('admin_user');
    const needsOnboarding = user && !isImpersonating && user.role === 'client' && (user.onboarding_step || 0) < 4 && !user.has_paid_access;
    const trialAccess = user && user.trial_enabled && !user.has_paid_access;
    const allowedDuringOnboarding = ['ecosystem_select', 'tier_select', 'client_onboarding', 'own_company_onboarding', 'demo', ...(trialAccess ? ['proforma'] : [])];
    
    if (needsOnboarding && !allowedDuringOnboarding.includes(newPage)) {
      setPageRaw('tier_select');
      return;
    }

    if (isBack) {
      // Going back — pop last item
      setPageHistory(prev => prev.length > 1 ? prev.slice(0, -1) : ['dashboard']);
    } else {
      // Going forward — push to history (max 20)
      setPageHistory(prev => [...prev.slice(-19), newPage]);
    }
    setPageRaw(newPage);
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWebLogin, setShowWebLogin] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [workAuths, setWorkAuths] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [kycLoading, setKycLoading] = useState(false);
  
  // Test mode - superuser acts as regular client
  const [testMode, setTestMode] = useState(() => {
    return localStorage.getItem('workifypl_test_mode') === 'true';
  });
  
  // Toggle test mode
  const toggleTestMode = useCallback(() => {
    setTestMode(prev => {
      const newVal = !prev;
      localStorage.setItem('workifypl_test_mode', newVal ? 'true' : 'false');
      return newVal;
    });
  }, []);

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.ready();
      tg.expand();
      try { tg.setHeaderColor('#1a1a2e'); } catch {}
      try { tg.setBackgroundColor('#0f0f23'); } catch {}
    }
    const params = new URLSearchParams(window.location.search);
    const screen = params.get('screen');
    if (screen) setPage(screen);
    doAuth();
  }, []);

  async function doAuth() {
    setLoading(true);
    setError(null);
    try {
      const u = await authenticate();
      setUser(u);

      // Load companies
      const [compRes, waRes, ctRes] = await Promise.all([
        api.get('/companies').catch(() => ({ data: [] })),
        api.get('/work-authorizations').catch(() => ({ data: [] })),
        api.get('/contracts').catch(() => ({ data: [] })),
      ]);
      setCompanies(compRes.data);
      setWorkAuths(waRes.data);
      setContracts(ctRes.data);

      // Auto-assign company
      if (compRes.data.length === 1) {
        setCurrentCompany(compRes.data[0]);
      } else if (compRes.data.length > 1) {
        // Check if user has memberships → use first company
        if (u.memberships?.length > 0) {
          const mc = compRes.data.find(c => c.id === u.memberships[0].company_id);
          if (mc) setCurrentCompany(mc);
        }
      }

      // New user → client onboarding flow (step-by-step)
      const params = new URLSearchParams(window.location.search);
      const screen = params.get('screen');
      const isImpersonatingNow = localStorage.getItem('impersonating') === 'true' || !!localStorage.getItem('admin_token') || !!localStorage.getItem('admin_user');
      if (!isImpersonatingNow && u.role === 'client' && (u.onboarding_step || 0) < 4 && !u.has_paid_access && !screen) {
        // New users start at tier selection (incubator vs own company)
        setPage('ecosystem_select');
      } else if (!isImpersonatingNow && u.profile_status === 'draft' && !u.phone && !u.email && u.role === 'client' && !screen) {
        setPage('onboarding');
      }
      // Superuser → always show mode selection at start
      const isSu = SUPERUSER_IDS.includes(u.telegram_id);
      if (isSu && !screen && localStorage.getItem('impersonating') !== 'true') {
        setPage('super_admin_select');
      }
    } catch (e) {
      console.error('Auth error:', e);
      if (e.message === 'NO_AUTH' || e.message === 'NO_TELEGRAM') {
        setShowWebLogin(true);
        setLoading(false);
        return;
      } else if (e.message === 'AUTH_EXPIRED') {
        setError('Сессия истекла. Закройте WebApp и откройте заново из бота.');
      } else if (e.response?.status === 401) {
        setError('Ошибка авторизации. Закройте и откройте приложение заново.');
      } else if (e.response?.status >= 500) {
        setError('Сервер временно недоступен. Попробуйте через минуту.');
      } else if (e.code === 'ERR_NETWORK') {
        setError('Нет соединения с сервером. Проверьте интернет.');
      } else {
        setError(e.response?.data?.detail || `Ошибка: ${e.message}`);
      }
    } finally {
      setLoading(false);
    }
  }

  const refreshUser = useCallback(async () => {
    try {
      const [waRes, ctRes] = await Promise.all([
        api.get('/work-authorizations').catch(() => ({ data: [] })),
        api.get('/contracts').catch(() => ({ data: [] })),
      ]);
      setWorkAuths(waRes.data);
      setContracts(ctRes.data);
    } catch {}
  }, []);

  async function startKyc() {
    try {
      setKycLoading(true);
      const { data } = await api.get('/kyc/session');
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      alert(e.userMessage || 'KYC недоступен');
    } finally {
      setKycLoading(false);
    }
  }

  if (loading) return <I18nProvider><Loading text="Подключаемся..." /></I18nProvider>;
  if (showWebLogin && !user) return <I18nProvider><WebLogin onLogin={() => { setShowWebLogin(false); doAuth(); }} /></I18nProvider>;
  if (error) return <I18nProvider><ErrorScreen message={error} onRetry={doAuth} /></I18nProvider>;
  if (!user) return <I18nProvider><ErrorScreen message="Не удалось войти" onRetry={doAuth} /></I18nProvider>;

  // Check if this is a real superuser (by telegram_id)
  const isSuperuser = SUPERUSER_IDS.includes(user.telegram_id);
  
  // isAdmin is false in test mode (superuser acts as regular client)
  const isAdmin = testMode ? false : ADMIN_ROLES.includes(user.role);

  const ecosystemMode = user?.ecosystem_mode
    || (user?.has_workify_accounting && !user?.has_workify_incubator && !user?.has_genro ? 'accounting'
      : user?.has_workify_incubator && !user?.has_workify_accounting && !user?.has_genro ? 'incubator'
      : user?.has_genro && !user?.has_workify_incubator && !user?.has_workify_accounting ? 'genro'
      : user?.has_genro || user?.has_workify_incubator || user?.has_workify_accounting ? 'combo'
      : (user?.account_type === 'own_company' ? 'accounting' : 'incubator'));

  const isInClientMode = user?._force_client_mode || localStorage.getItem('superadmin_as_client') === 'true';
  const isSuperadmin = !isInClientMode && (user?.role === 'superadmin' || user?._original_role === 'superadmin' || (() => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
      return adminUser?.role === 'superadmin';
    } catch { return false; }
  })());
  // Superadmin override: force combo mode for full access
  const effectiveEcosystemMode = isSuperadmin ? 'combo' : ecosystemMode;
  const productAccess = {
    genro: !!(user?.has_genro || effectiveEcosystemMode === 'genro' || effectiveEcosystemMode === 'combo'),
    incubator: !!(isSuperadmin || user?.has_workify_incubator || effectiveEcosystemMode === 'incubator' || effectiveEcosystemMode === 'combo' || (!user?.ecosystem_mode && user?.account_type !== 'own_company')),
    accounting: !!(isSuperadmin || user?.has_workify_accounting || effectiveEcosystemMode === 'accounting' || effectiveEcosystemMode === 'combo' || user?.account_type === 'own_company' || user?.role === 'company_owner'),
  };

  const KYC_DISABLED = true; // TEMP: disable KYC globally
  const kycRequired = !KYC_DISABLED && user?.has_paid_access && user?.profile_status !== 'kyc_verified';

  // Check statuses for current company
  const hasWorkAuth = currentCompany && workAuths.some(
    wa => wa.company_id === currentCompany.id && wa.status === 'active'
  );
  const hasContract = currentCompany && contracts.some(
    ct => ct.company_id === currentCompany.id && ['signed', 'active'].includes(ct.status)
  );

  // Company owners keep their existing home; delegated accountants start with daily work.
  const isAccountant = !testMode && !isInClientMode && (
    ['accountant', 'company_admin'].includes(user?.role)
    || (user?.role !== 'superadmin' && user?.role !== 'company_owner' && user?.account_type !== 'own_company'
      && [...companies, ...(user?.memberships || [])].some(c =>
        (c.id === currentCompany?.id || c.company_id === currentCompany?.id || !currentCompany)
        && ['accountant', 'company_admin'].includes(c.company_role || c.member_role || c.role)))
  );

  // Full-books posting remains unpublished until its separate migration is approved.
  const companyBooksEnabled = false;
  const pages = {
    accountant_reports: (isAccountant || user?.role === 'superadmin') ? <AccountantReports key={currentCompany?.id || 'none'} /> : <DashboardOwner />,
    accountant_dashboard: isAccountant ? <AccountantDashboard /> : <AccountingOverview key={currentCompany?.id || 'all'} />,
    accounting_overview: isAccountant ? <AccountantDashboard /> : (companyBooksEnabled ? <AccountingOverview key={currentCompany?.id || 'all'} /> : <DashboardOwner />),
    company_books: companyBooksEnabled ? <CompanyBooks key={currentCompany?.id || 'none'} /> : (isAccountant ? <AccountantDashboard /> : <DashboardOwner />),
    super_admin_select: isSuperuser ? <SuperAdminSelect /> : <Dashboard />,
    dashboard: isAccountant ? <AccountantDashboard /> : (((user?.account_type === 'own_company' || user?.role === 'company_owner' || user?.role === 'superadmin' || (productAccess.accounting && !productAccess.incubator && !productAccess.genro)) && !testMode) ? <DashboardOwner /> : <Dashboard />),
    ecosystem_select: <EcosystemModeSelect />,
    tier_select: <TierSelect />,
    client_onboarding: <ClientOnboarding />,
    own_company_onboarding: <OwnCompanyOnboarding />,
    company_team: <CompanyTeam />,
    hr_employees: <HREmployees />,
    hr_leaves: <HRLeaves />,
    zus_dashboard: <ZusDashboard />,
    admin_tier2: <AdminTier2 />,
    onboarding: <OnboardingFlow />,
    onboarding_old: <Onboarding />,
    profile: <Profile />,
    invoices_out: ((user?.account_type === 'own_company' || user?.role === 'company_owner' || user?.role === 'superadmin') && !testMode) ? <OwnerInvoices /> : <Proforma />,
    invoices_in: <InvoicesIn />,
    cash: <Cash />,
    my_finance: <MyFinance />,
    my_taxes: <MyTaxes />,
    applications: <Applications />,
    karta_pobytu: <KartaPobytu />,
    karta_pobytu_new: <KartaPobytuNew />,
    super_admin_finance: isAdmin ? <SuperAdminFinance /> : <Dashboard />,
    documents: <Documents />,
    company_select: <CompanySelect />,
    contract_sign: <ContractSign />,
    gateway_sign: <GatewaySign />,
    work_contract: <MyContracts />,
    my_contracts: <MyContracts />,
    withdrawals: <Withdrawals />,
    top_up: <TopUp />,
    exchange: <Exchange />,
    my_team: <MyTeam />,
    payment_links: <PaymentLinks />,
    support: <Support />,
    admin_support: <AdminSupport />,
    transactions: <Transactions />,
    receipts: <Receipts />,
    admin_receipts: <AdminReceipts />,
    admin_accounting: <AdminAccounting />,
    admin_bank_import: <AdminBankImport />,
    tax_dashboard: <TaxDashboard />,
    my_dashboard: <MyDashboard />,
    admin_incoming_invoices: isAdmin ? <AdminIncomingInvoices /> : <Dashboard />,
    guide: <Guide />,
    ksef_settings: <KsefSettings />,
    stripe_settings: <StripeSettings />,
    bank_accounts: <BankAccounts />,
    demo: <DemoMode />,
    analytics: <Analytics />,
    achievements: <Achievements />,
    leaderboard: <Leaderboard />,
    qr_payment: <QRPayment />,
    calendar: <Calendar />,
    invoice_templates: <InvoiceTemplates />,
    subscriptions: <Subscriptions />,
    voice_invoice: <VoiceInvoice />,
    ai_calls: <AiCalls />,
    company_roles: <CompanyRoles />,
    api_docs: <ApiDocs />,
    country_select: <CountrySelect />,
    proforma: <Proforma />,
    owner_invoices: <OwnerInvoices key={currentCompany?.id || 'all'} />,
    owner_cit8: <OwnerCit8 />,
    accounting_restore: <AccountingRestore />,
    a1_application: <A1Application />,
    counterparties: <Counterparties />,
    partner_contracts: <PartnerContracts />,
    work_permit: <WorkPermits />,
    admin_work_permits: <WorkPermits />,
    contract_templates: <ContractTemplates />,
    legal_documents: <LegalDocuments />,
    monthly_rachunki: <MonthlyRachunki />,
    ai_assistant: <AiAssistant />,
    ai_lawyer: <AiChat mode="lawyer" />,
    ai_accountant: <AiChat mode="accountant" />,
    ai_sales: <AiChat mode="sales" />,
    admin_clients: isAdmin ? <AdminClients /> : <Dashboard />,
    admin_companies: isAdmin ? <AdminCompanies /> : <Dashboard />,
    admin_applications: isAdmin ? <AdminApplications /> : <Dashboard />,
    admin_requests: isAdmin ? <AdminRequests /> : <Dashboard />,
    admin_contracts: isAdmin ? <AdminContracts /> : <Dashboard />,
    admin_partner_contracts: isAdmin ? <AdminPartnerContracts /> : <Dashboard />,
    admin_payouts: isAdmin ? <AdminPayouts /> : <Dashboard />,
    admin_diagnostic: isAdmin ? <AdminDiagnostic /> : <Dashboard />,
    admin_audit: isAdmin ? <AdminAudit /> : <Dashboard />,
    admin_metrics: isAdmin ? <AdminMetrics /> : <Dashboard />,
    notifications: <Notifications />,
    admin_analytics: isAdmin ? <AdminAnalytics /> : <Dashboard />,
    admin_users: isAdmin ? <AdminUsers /> : <Dashboard />,
    admin_hierarchy: isAdmin ? <AdminHierarchy /> : <Dashboard />,
    payment_wall: <PaymentWall />,
    admin_gateway_payments: isAdmin ? <AdminGatewayPayments /> : <Dashboard />,
    admin_invoices: isAdmin ? <AdminInvoices /> : <Dashboard />,
    admin_broadcast: isAdmin ? <AdminBroadcast /> : <Dashboard />,
    my_analytics: <MyAnalytics />,
    security: <Security />,
    offers: <OffersList />,
    offer_publish: <OfferPublish />,
    offer_details: <OfferDetails />,
    offer_accept: <OfferAccept />,
    genro_entry: <GenroEntry />,
  };

  if (isAccountant) {
    ['accountant_company', 'accountant_sales', 'accountant_purchases', 'accountant_journal', 'accountant_bank', 'accountant_documents', 'accountant_team', 'owner_invoices', 'bank_accounts', 'hr_employees', 'documents', 'my_finance', 'my_taxes'].forEach(key => { pages[key] = <AccountantDashboard />; });
  }

  return (
    <I18nProvider>
      <AppContext.Provider value={{
        user, setUser, page, setPage, pageHistory, isAdmin, isAccountant, companyBooksEnabled, effectiveEcosystemMode, productAccess,
        companies, currentCompany, setCurrentCompany,
        workAuths, contracts, hasWorkAuth, hasContract,
        refreshUser,
        // Superuser test mode
        isSuperuser, testMode, toggleTestMode,
      }}>
        <div className="app">
          {localStorage.getItem('impersonating') === 'true' && (
            <div style={{
              background: '#7c3aed', color: '#fff', padding: '8px 16px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              fontSize: '13px', fontWeight: 600, zIndex: 9999,
            }}>
              <span>👤 Вы смотрите как: {user?.first_name} {user?.last_name} (ID: {user?.id})</span>
              <button
                onClick={() => {
                  if (returnToAdmin()) {
                    window.location.reload();
                  }
                }}
                style={{
                  background: '#fff', color: '#7c3aed', border: 'none',
                  borderRadius: 6, padding: '4px 12px', cursor: 'pointer',
                  fontWeight: 600, fontSize: '12px',
                }}
              >
                ← Вернуться к админу
              </button>
            </div>
          )}

          {kycRequired && (
            <div style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              background: 'rgba(0,0,0,0.85)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 20,
            }}>
              <div style={{
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 16,
                padding: 24,
                textAlign: 'center',
                maxWidth: 420,
              }}>
                <div style={{ fontSize: 42, fontWeight: 800, letterSpacing: 2 }}>KYC</div>
                <div style={{ margin: '12px 0', opacity: 0.8 }}>
                  Чтобы продолжить — пройди верификацию. До этого действия заблокированы.
                </div>
                <button className="btn btn-primary" onClick={startKyc} disabled={kycLoading}>
                  {kycLoading ? '⏳ Открываю KYC...' : '✅ Пройти KYC'}
                </button>
              </div>
            </div>
          )}

          {/* Language switcher — floating top-right on every page */}
          <div style={{ position: 'fixed', top: 56, right: 8, zIndex: 9999 }}>
            <LanguageSwitcher />
          </div>

          {(isAccountant || ['superadmin', 'company_admin', 'company_owner', 'accountant'].includes(user?.role) || user?.account_type === 'own_company' || companies.some(c => ['accountant', 'company_admin', 'owner', 'admin'].includes(c.company_role || c.member_role))) && !testMode ? (
            <AccountingWorkspace><div className="page-content">{pages[page] || pages.dashboard}</div></AccountingWorkspace>
          ) : <div className="page-content">{pages[page] || pages.dashboard}</div>}
          {page !== 'ecosystem_select' && page !== 'tier_select' && page !== 'client_onboarding' && page !== 'own_company_onboarding' && page !== 'demo' && <BottomNav />}
        </div>
      </AppContext.Provider>
    </I18nProvider>
  );
}
